import {
  useEffect, useMemo, useState,
} from 'react';
import MultiSelect from 'library/components/multiselect';
import Dropdown from 'library/components/dropdown';
import { FILTER_KEYS, SORT_DIRECTION as DIRECTION } from 'store/barcode-items/fixtures';
import useCategoryList from 'library/hooks/use-deferred-data/use-barcode-category-list';
import useManufacturerList from 'library/hooks/use-deferred-data/use-barcode-manufacturer-list';
import {
  Container,
  Reset,
  Icon,
  Wrapper,
} from './styles';
import { BARCODE_SORTING_OPTIONS } from './constants';
import { getBarcodeFilterValues, getBarcodeSortingValue } from './helpers';
import SearchQuery from '../search-query';

const {
  CATEGORIES,
  SORT_DIRECTION,
  MANUFACTURERS,
} = FILTER_KEYS;

const getFilterQueryData = ({
  value,
  type,
  valueMap,
}) => (value || []).map((id) => ({ type, value: id, text: valueMap[id] }));

const DEFERRED_FILTERS = [MANUFACTURERS, CATEGORIES];
const DEFAULT_SKIP_STATE = {
  [MANUFACTURERS]: true,
  [CATEGORIES]: true,
};

const Filters = ({
  onChange,
  onReset,
  onDelete,
  isLoading,
  filters,
  isShown,
  isLongerModal = false,
  baseQuery,
  children,
  isQueryDisplayed = true,
  isQueryFilterSibling = true,
}) => {
  const [isSkipped, setDataLoaded] = useState(DEFAULT_SKIP_STATE);
  const {
    [MANUFACTURERS]: manufacturersValue,
    [CATEGORIES]: categoryValue,
  } = filters;
  const { options: categoriesOptions, labelMap: categoryNames, onQueryChange: onCategoryQueryChange } = useCategoryList(
    isSkipped[CATEGORIES],
    {},
    false,
  );
  const { options: manufacturersOptions, labelMap: manufacturerNames, onQueryChange: onManufacturerQueryChange } = useManufacturerList(
    isSkipped[MANUFACTURERS],
    {},
    false,
  );

  const handleOpen = (field) => {
    if(isSkipped[field] === true) {
      setDataLoaded({
        ...isSkipped,
        [field]: false,
      });
    }
  };

  const handleChange = (field, value) => {
    const updatedFilterValues = getBarcodeFilterValues({ field, value });

    onChange(updatedFilterValues);
  };

  const sortingValue = getBarcodeSortingValue({
    [SORT_DIRECTION]: filters[FILTER_KEYS.SORT_DIRECTION],
  });

  useEffect(() => {
    const result = {
      ...isSkipped,
    };

    DEFERRED_FILTERS.forEach((key) => {
      if (Array.isArray(filters[key]) && filters[key]?.length === 0 && isSkipped[key] === false) {
        result[key] = true;
      }
    });

    if(Object.keys(result).some((key) => result[key] !== isSkipped[key])) {
      setDataLoaded(result);
    }
  }, [categoryValue?.length, manufacturersValue?.length]);

  const categoriesQuery = useMemo(() => getFilterQueryData({ type: CATEGORIES, value: filters[CATEGORIES], valueMap: categoryNames }), [categoryValue]);
  const manufacturersQuery = useMemo(() => getFilterQueryData({ type: MANUFACTURERS, value: filters[MANUFACTURERS], valueMap: manufacturerNames }), [manufacturersValue]);

  const query = [...baseQuery, ...categoriesQuery, ...manufacturersQuery];

  return (
    <>
      {
        isShown ? (
          <>
            {
              isQueryDisplayed && query.length ? (
                <SearchQuery
                  data={query}
                  onDelete={onDelete}
                  onReset={onReset}
                  isEditable
                  isLoading={isLoading}
                />
              ) : null
            }
            <Wrapper style={{ display: 'flex', flexDirection: isQueryFilterSibling ? 'column' : 'row', width: '100%' }}>
              <Container isLongerModal={isLongerModal}>
                <Dropdown
                  label="Sort by"
                  options={BARCODE_SORTING_OPTIONS}
                  value={sortingValue}
                  onChange={(value) => handleChange(SORT_DIRECTION, value)}
                />
                <MultiSelect
                  label="Category"
                  key={CATEGORIES}
                  field={CATEGORIES}
                  searchPlaceholder="Search within categories"
                  options={categoriesOptions}
                  value={categoryValue}
                  labelMap={categoryNames}
                  onChange={handleChange}
                  onOpen={handleOpen}
                  onSearch={onCategoryQueryChange}
                />
                <MultiSelect
                  label="Manufacturer"
                  key={MANUFACTURERS}
                  field={MANUFACTURERS}
                  searchPlaceholder="Search within manufacturers"
                  options={manufacturersOptions}
                  value={manufacturersValue}
                  onChange={handleChange}
                  onOpen={handleOpen}
                  onSearch={onManufacturerQueryChange}
                />
                {
                  (query.length || filters[SORT_DIRECTION] !== DIRECTION.NAME) ? (
                    <Reset onClick={onReset}>
                      <em>Reset filters</em>
                      <span>
                        <Icon className="icon-reload" />
                      </span>
                    </Reset>
                  ) : null
                }
              </Container>
              {children}
            </Wrapper>
          </>
        ) : null
      }
    </>
  );
};

export default Filters;
